import { groupErrorsByTarget } from '@wix/form-validator';
import { formFieldFocus } from '@wix/bi-logger-forms-platform-uou/v2';
import React, { useCallback, useMemo, useRef } from 'react';
import { useExternalData } from '../../contexts/external-data-context';
import { useFormFields } from '../../contexts/form-fields-context';
import { useBi } from '../../../../contexts/bi-context';
import { useFieldValidation } from '../../../../contexts/field-validation-context';
import { useForm } from '../../../../contexts/form-context';
import { useError } from '../../../../field-error';
import { FieldPropsContext } from '../../../../contexts/field-props-context';
import { FIELD_PROPS_MAPPERS, BaseMapper } from '../../../../mappers/public-props-map';
import { useFieldAttributes } from '../../../../services/field-attributes';
import { FieldAttributesContext } from '../../../../contexts/field-attributes-context';
export const InputField = _ref => {
  var _field$properties, _field$properties2;
  let {
    field,
    errors,
    values,
    onFieldChange,
    onBlur,
    onFocus,
    FieldLayout
  } = _ref;
  const bi = useBi();
  const componentsById = useFormFields();
  const externalData = useExternalData();
  const {
    validateFields
  } = useFieldValidation();
  const form = useForm();
  const fieldValue = values[field.target];
  const fieldValueRef = useRef(fieldValue);
  fieldValueRef.current = fieldValue;
  const errorsByTarget = useMemo(() => groupErrorsByTarget(errors), [errors]);
  const fieldErrors = errorsByTarget == null ? void 0 : errorsByTarget[field.target];
  const fieldError = fieldErrors == null ? void 0 : fieldErrors[0];
  const {
    requiredIndicator,
    requiredIndicatorPlacement
  } = form.requiredIndicatorProperties || {};
  const propsMapper = FIELD_PROPS_MAPPERS[field.fieldType] || BaseMapper;
  const _onBlur = useCallback(() => {
    const currentFieldValue = fieldValueRef.current;
    bi == null || bi.report(formFieldFocus({
      action_type: 'blur',
      field_id: field.id,
      field_label: field.properties.label,
      field_target: field == null ? void 0 : field.target
    }));
    validateFields([{
      value: currentFieldValue,
      path: field.path
    }]);
    onBlur == null || onBlur(field.path);
  }, [bi, field.id, field.properties.label, field.target, field.path, validateFields, onBlur]);
  const _onFocus = () => {
    bi == null || bi.report(formFieldFocus({
      action_type: 'focus',
      field_id: field.id,
      field_label: field.properties.label,
      field_target: field == null ? void 0 : field.target
    }));
    onFocus == null || onFocus(field.path);
  };
  const handleChange = useCallback(value => {
    if (fieldError) {
      validateFields([{
        value,
        path: field.path
      }]);
    }
    if (!value && value !== 0 && typeof value !== 'boolean' || Array.isArray(value) && value.length === 0) {
      value = null;
    }
    onFieldChange == null || onFieldChange(field.target, value);
  }, [onFieldChange, field, fieldError, validateFields]);
  const {
    hasError,
    errorMessage
  } = useError({
    error: fieldError,
    customErrors: (_field$properties = field.properties) == null ? void 0 : _field$properties.customErrors,
    fieldType: field.fieldType,
    properties: field.properties,
    validationError: fieldError
  });
  const FieldComponent = componentsById[field.fieldType];
  const fieldProps = {
    ...field.properties,
    readOnly: Boolean((_field$properties2 = field.properties) == null ? void 0 : _field$properties2.readOnly),
    onChange: handleChange,
    onBlur: _onBlur,
    onFocus: _onFocus,
    onFieldBlur: onBlur,
    onFieldFocus: onFocus,
    hasError,
    errorMessage,
    error: fieldError,
    errors: fieldErrors,
    value: fieldValue,
    id: field.id,
    target: field.target,
    path: field.path,
    externalData,
    fieldType: field.fieldType,
    FieldLayout,
    requiredIndicatorType: requiredIndicator,
    requiredIndicatorPlacement
  };
  const fieldAttributes = useFieldAttributes(fieldProps.id, fieldProps.error, fieldProps.description);
  if (!FieldComponent) {
    return null;
  }
  return /*#__PURE__*/React.createElement(FieldAttributesContext.Provider, {
    value: fieldAttributes
  }, /*#__PURE__*/React.createElement(FieldPropsContext.Provider, {
    value: {
      ...propsMapper({
        ...field.properties,
        id: field.id,
        target: field.target,
        path: field.path,
        error: fieldError,
        errors: fieldErrors,
        value: fieldValue,
        externalData,
        fieldType: field.fieldType,
        requiredIndicatorType: requiredIndicator,
        requiredIndicatorPlacement
      }),
      onChange: handleChange,
      onBlur: _onBlur,
      onFocus: _onFocus,
      hasError,
      errorMessage,
      v2: fieldProps
    }
  }, /*#__PURE__*/React.createElement(FieldComponent, fieldProps)));
};
//# sourceMappingURL=input-field.js.map